// src/components/ui/Modal/Modal.tsx
import React, { useState, useRef, forwardRef } from 'react';
import ReactModal from 'react-modal';

import * as DraggableNamespace from 'react-draggable';
import * as ResizableNamespace from 're-resizable';

import { useSwipeable } from 'react-swipeable';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './Modal.module.css';

// Обертки для корректной работы
const Draggable = DraggableNamespace.default as unknown as React.FC<any>;
const Resizable = ResizableNamespace.Resizable as unknown as React.FC<any>;

// Устанавливаем корневой элемент для модальных окон
if (typeof window !== 'undefined') {
  ReactModal.setAppElement('#root');
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  heightType?: 'auto' | 'fixed' | 'screen70' | 'screen80' | 'screen90';
  allowBackdropClose?: boolean;
  className?: string;
  mobileMode?: 'fullscreen' | 'bottom-sheet' | 'normal';
  mobileHeight?: 'auto' | 'full' | 'half' | 'third' | 'twoThirds';
  swipeToDismiss?: boolean;
  preventScroll?: boolean;
  draggable?: boolean;
  resizable?: boolean;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  defaultWidth?: number;
  defaultHeight?: number;
}

const DraggableComponent = forwardRef<HTMLDivElement, { children: React.ReactNode, className: string }>(
  ({ children, className }, ref) => (
    <div ref={ref} className={className}>
      {children}
    </div>
  )
);
DraggableComponent.displayName = 'DraggableComponent';

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  heightType = 'screen80',
  allowBackdropClose = true,
  className = '',
  mobileMode = 'normal',
  mobileHeight = 'auto',
  swipeToDismiss = false,
  preventScroll = true,
  draggable = true,
  resizable = false,
  minWidth = 200,
  minHeight = 200,
  maxWidth = 1200,
  maxHeight = 800,
  defaultWidth = 600,
  defaultHeight = 400,
}) => {
  const { isMobile, isLandscape } = useResponsive();
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const draggableRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const getEffectiveSize = () => {
    if (resizable && !isMobile) return null;
    if (isMobile && (mobileMode === 'fullscreen' || mobileMode === 'bottom-sheet')) return 'full';
    if (isMobile && mobileMode === 'normal') {
      if (size === 'sm' || size === 'md') return 'lg';
      return size;
    }
    return size;
  };

  const getEffectiveHeightType = () => {
    if (resizable && !isMobile) return null;
    if (isMobile && mobileMode === 'fullscreen') return 'screen90';
    if (isMobile && mobileMode === 'bottom-sheet') {
      if (mobileHeight === 'half') return 'screen50';
      if (mobileHeight === 'third') return 'screen30';
      if (mobileHeight === 'twoThirds') return 'screen70';
      if (mobileHeight === 'full') return 'screen90';
      return heightType;
    }
    if (isMobile && isLandscape) return 'screen80';
    return heightType;
  };

  const getSizeStyle = () => {
    const effectiveSize = getEffectiveSize();
    if (!effectiveSize) return '';
    const sizeMap: Record<string, string> = {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
      xl: styles.xl,
      '2xl': styles.xl2,
      '3xl': styles.xl3,
      '4xl': styles.xl4,
      '5xl': styles.xl5,
      full: styles.full,
    };
    return sizeMap[effectiveSize] || styles.md;
  };

  const getHeightStyle = () => {
    const effectiveHeightType = getEffectiveHeightType();
    if (!effectiveHeightType) return '';
    const heightMap: Record<string, string> = {
      auto: styles.heightAuto,
      fixed: styles.heightFixed,
      screen30: styles.heightScreen30,
      screen50: styles.heightScreen50,
      screen70: styles.heightScreen70,
      screen80: styles.heightScreen80,
      screen90: styles.heightScreen90,
    };
    return heightMap[effectiveHeightType] || styles.heightScreen80;
  };

  const swipeHandlers = useSwipeable({
    onSwipedDown: (eventData) => {
      if (swipeToDismiss && isMobile && eventData.velocity > 0.5) {
        setIsClosing(true);
        setTimeout(() => {
          onClose();
          setIsClosing(false);
        }, 300);
      }
    },
    trackMouse: false,
    delta: 100,
  });

  const handleDragStart = () => setIsDragging(true);
  const handleDragStop = () => setIsDragging(false);

  const handleBackdropClick = () => {
    if (allowBackdropClose) {
      onClose();
    }
  };

  const modalStyles = {
    content: {
      position: 'relative' as const,
      inset: 'auto',
      border: 'none',
      background: 'transparent',
      overflow: 'visible',
      borderRadius: 0,
      padding: 0,
      width: '100%',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(2px)',
      display: 'flex',
      alignItems: isMobile ? 'flex-end' : 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: isMobile ? '0' : '1rem',
      overflow: 'auto',
    },
  };

  const shouldUseDraggable = draggable && !isMobile;
  const shouldUseResizable = resizable && !isMobile;

  const modalContent = (
    <div
      ref={modalRef}
      className={`
        ${styles.modal}
        ${getSizeStyle()}
        ${getHeightStyle()}
        ${isMobile && mobileMode === 'fullscreen' ? styles.fullscreen : ''}
        ${isMobile && mobileMode === 'bottom-sheet' ? styles.bottomSheet : ''}
        ${isDragging ? styles.dragging : ''}
        ${isClosing ? styles.closing : ''}
        ${className}
      `}
    >
      {swipeToDismiss && isMobile && (
        <div className={styles.swipeHandle}>
          <div className={styles.swipeHandleBar}></div>
        </div>
      )}
      {shouldUseDraggable && (
        <div className={styles.dragHandle}>
          <div className={styles.dragHandleBar}></div>
        </div>
      )}
      {children}
    </div>
  );

  const resizableContent = shouldUseResizable ? (
    <Resizable
      defaultSize={{
        width: defaultWidth,
        height: defaultHeight,
      }}
      minWidth={minWidth}
      minHeight={minHeight}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      className={styles.resizable}
      enable={{
        top: true,
        right: true,
        bottom: true,
        left: true,
        topRight: true,
        bottomRight: true,
        bottomLeft: true,
        topLeft: true,
      }}
    >
      {modalContent}
    </Resizable>
  ) : (
    modalContent
  );

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleBackdropClick}
      style={modalStyles}
      preventScroll={preventScroll}
      closeTimeoutMS={300}
    >
      {shouldUseDraggable ? (
        <Draggable
          nodeRef={draggableRef}
          handle={`.${styles.dragHandle}`}
          onStart={handleDragStart}
          onStop={handleDragStop}
          bounds="parent"
        >
          <DraggableComponent ref={draggableRef} className={styles.draggableWrapper}>
            {resizableContent}
          </DraggableComponent>
        </Draggable>
      ) : (
        <div {...(swipeToDismiss && isMobile ? swipeHandlers : {})}>
          {resizableContent}
        </div>
      )}
    </ReactModal>
  );
};

export default Modal;
